import { ScanSearch, CalendarCheck2, Layers, Database, FileCheck2, RefreshCw } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const SERVICES = [
  {
    icon: ScanSearch,
    title: "Patent Data Validation",
    points: [
      "Validate bibliographic and application information",
      "Identify inconsistent or incomplete patent records",
      "Verify critical patent data fields",
    ],
  },
  {
    icon: CalendarCheck2,
    title: "US Patent Deadline Verification",
    points: [
      "Review critical US patent prosecution deadlines",
      "Validate deadline information against available patent records",
      "Help identify potential discrepancies requiring attorney review",
    ],
  },
  {
    icon: Layers,
    title: "Patent Portfolio Data Review",
    points: [
      "Review large patent portfolios",
      "Identify missing, inconsistent, or duplicate information",
      "Improve portfolio data quality",
    ],
  },
  {
    icon: Database,
    title: "IP Database Validation",
    points: [
      "Validate patent management system records",
      "Compare source information with internal databases",
      "Support data cleansing and quality-control initiatives",
    ],
  },
  {
    icon: FileCheck2,
    title: "Patent Document & Record Verification",
    points: [
      "Application, patent numbers, filing dates & priority data",
      "Inventor and applicant/assignee information",
      "Status and related bibliographic data",
    ],
  },
  {
    icon: RefreshCw,
    title: "Ongoing IP Data Quality Support",
    points: [
      "Recurring validation services & portfolio audits",
      "Data quality checks and backlog validation",
      "Quality-control support for IP operations teams",
    ],
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <SectionLabel index="01">Services</SectionLabel>
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <h2 className="lg:col-span-8 font-display font-bold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
              Patent data validation services, built for legal operations
            </h2>
            <p className="lg:col-span-4 text-slate-600 text-base leading-relaxed">
              Systematic validation and verification support across the full patent data
              lifecycle — from single records to entire portfolios.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-slate-200">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07}>
              <article
                data-testid={`service-card-${i + 1}`}
                className="group h-full border-r border-b border-slate-200 p-8 lg:p-10 hover:bg-slate-50 hover:-translate-y-1 transition-[background-color,transform] duration-500"
              >
                <div className="flex items-start justify-between mb-8">
                  <s.icon className="w-7 h-7 text-navy group-hover:text-accent transition-colors duration-300" strokeWidth={1.5} />
                  <span className="font-monodata text-xs text-slate-300 group-hover:text-accent transition-colors duration-300">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display font-bold text-navy text-lg mb-5 tracking-tight">{s.title}</h3>
                <ul className="space-y-2.5">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                      <span className="mt-2 w-3 h-px bg-accent shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
